import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, map, Observable, of, tap } from 'rxjs';
import { SendLeadModalEnum } from 'src/app/_enums/send-lead-modal.enum';
import { CustomerSupportService } from 'src/app/_services/customer-support.service';
import { ProposalService } from 'src/app/_services/proposal.service';
import { Admin } from 'src/app/_types/auth.types';
import { Lead, SendGroupeMutuelLeadRequest } from 'src/app/_types/lead.types';
import { GROUPE_MUTUEL_BASE_PRODUCT_CODES } from 'src/app/constants/insurance/health.constants';
import { getSendLeadModal } from 'src/app/Dashboard/proposals/task-details/components/task-history/stepper-actions/send-lead/send-lead-modal.helpers';

import { SpinnerComponent } from '../../../spinner/spinner.component';
import { SendLeadBase } from '../send-lead.base';

@Component({
  selector: 'app-send-groupe-mutuel-lead-modal',
  templateUrl: './send-groupe-mutuel-modal.component.html',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, SpinnerComponent],
})
export class SendGroupeMutuelLeadModalComponent
  extends SendLeadBase
  implements OnInit
{
  @ViewChild('content', { static: true }) content!: TemplateRef<HTMLElement>;

  @Input()
  isStandalone = true;

  modalRef!: NgbModalRef;

  customerSupportList: Admin[] = [];

  loadingCustomerSupportList = false;

  loadingRelatedLeads = false;

  relatedLeads: Lead[] = [];

  submitted = false;

  isAttendingDoctorNumberRequired = false;

  form = this.fb.group({
    customerSupportId: this.fb.control('', [Validators.required]),
    consents: this.fb.group({
      identificationArticle45: this.fb.control(false),
      coveragePremiumInfo: this.fb.control(false),
      dutiesAltModel: this.fb.control(false),
      discountsComboFamily: this.fb.control(false),
      overviewProvided: this.fb.control(false),
      generalConditionsProvided: this.fb.control(false),
      art45InfoSheetProvided: this.fb.control(false),
      specialConditionsProvided: this.fb.control(false),
      startDurationTermination: this.fb.control(false),
      waitingPeriodsHospitalMaternity: this.fb.control(false),
      revocationRight: this.fb.control(false),
      formalApplication: this.fb.control(false),
      signedScheduledAppointment: this.fb.control(false),
    }),
    attendingDoctorNumber: this.fb.control<string | null>(null),
  });

  constructor(
    private readonly fb: FormBuilder,
    private readonly modalService: NgbModal,
    private readonly toastrService: ToastrService,
    private readonly customerSupportService: CustomerSupportService,
    private readonly proposalService: ProposalService
  ) {
    super();
  }

  ngOnInit(): void {
    this.fetchGroupeMutuelCustomerSupportList();
    this.handleAttendingDoctorCondition();
  }

  openModal() {
    if (!this.canSendLeadToInsurer()) {
      this.toastrService.error(
        $localize`Cette demande ne peut pas être envoyée à Groupe Mutuel`
      );
      return;
    }

    this.resetForm();
    this.fetchRelatedLeads();
    this.fetchGroupeMutuelCustomerSupportList();
    this.modalRef = this.modalService.open(this.content, {
      scrollable: true,
      size: 'lg',
      centered: true,
    });
  }

  closeModal() {
    if (this.modalRef) {
      this.modalRef.close();
    }
  }

  private resetForm() {
    this.form.reset();
    this.submitted = false;
  }

  private fetchGroupeMutuelCustomerSupportList() {
    this.loadingCustomerSupportList = true;
    this.customerSupportService.getGroupeMutuelCustomerSupportList().subscribe({
      next: (res) => {
        this.customerSupportList =
          res?.data?.customerSupportAgentsList?.sort((x, y) =>
            `${x.firstName} ${x.lastName}`.localeCompare(
              `${y.firstName} ${y.lastName}`
            )
          ) ?? [];
        this.loadingCustomerSupportList = false;
      },
      error: () => {
        this.loadingCustomerSupportList = false;
        this.toastrService.error(
          $localize`Une erreur est survenue lors du chargement des chargés de clientèle`
        );
      },
    });
  }

  private fetchRelatedLeads() {
    this.relatedLeads = [];

    if (!this.lead?.leadId) {
      return;
    }

    this.loadingRelatedLeads = true;
    this.proposalService
      .getRelatedLeads(this.lead.leadId)
      .pipe(
        finalize(() => {
          this.loadingRelatedLeads = false;
        })
      )
      .subscribe({
        next: (res) => {
          this.relatedLeads = res?.data?.leadsList ?? [];
          this.handleAttendingDoctorCondition();
        },
        error: () => {
          this.relatedLeads = [];
          this.handleAttendingDoctorCondition();
          console.error('Error when loading linked leads');
        },
      });
  }

  override sendLeadToInsurer(): Observable<boolean> {
    if (!this.canSubmit()) return of(false);

    this.submitted = true;

    const payload: SendGroupeMutuelLeadRequest = {
      leadId: this.lead.leadId,
      ...this.form.getRawValue(),
    };

    return this.proposalService.sendGroupeMutuelLead(payload).pipe(
      map((res) => !res?.error),
      tap((success) => {
        if (!success) return;
        this.toastrService.success(
          $localize`Offre envoyée à Groupe Mutuel avec succès`
        );
        this.sent.emit();
        this.closeModal();
      }),
      catchError((e) => {
        console.log(e);
        this.toastrService.error(
          e.error?.error?.clientErrorMessageInFrench ??
            $localize`Une erreur est survenue. Veuillez réessayer ultérieurement`
        );
        return of(false);
      }),
      finalize(() => {
        this.submitted = false;
      })
    );
  }

  canSubmit() {
    return (
      !this.submitted &&
      !this.loadingCustomerSupportList &&
      !this.loadingRelatedLeads &&
      this.canSendLeadToInsurer() &&
      !!this.lead?.leadId &&
      this.form.valid
    );
  }

  override canSendLeadToInsurer(): boolean {
    return getSendLeadModal(this.lead) === SendLeadModalEnum.GROUPE_MUTUEL;
  }

  handleAttendingDoctorCondition() {
    const leadsToCheck = [this.lead, ...this.relatedLeads].filter(Boolean);
    this.isAttendingDoctorNumberRequired = leadsToCheck.some(
      (lead) =>
        lead?.metadata?.chosenBaseOffer?.productIds?.length > 0 &&
        lead?.metadata?.chosenBaseOffer?.productIds?.some(
          (x) =>
            x
              ?.toLocaleUpperCase()
              .includes(GROUPE_MUTUEL_BASE_PRODUCT_CODES.OPTIMED) ||
            x
              ?.toLocaleUpperCase()
              .includes(GROUPE_MUTUEL_BASE_PRODUCT_CODES.PRIMACARE)
        )
    );
    if (this.isAttendingDoctorNumberRequired) {
      this.form.controls.attendingDoctorNumber.addValidators(
        Validators.required
      );
    } else {
      this.form.controls.attendingDoctorNumber.clearValidators();
      this.form.controls.attendingDoctorNumber.reset();
    }
    this.form.updateValueAndValidity();
  }
}
